import { Tooltip } from "antd"
import React from "react"
import { useNavigate } from "react-router-dom"

import { TABULAR, homeCardStyle, homeColor, homeText } from "../home-theme.ts"
import type { HomeCardEdit } from "./HomeCard.tsx"
import type { TodayCount } from "./HomeTodayBand.tsx"
import { InfoIcon } from "./home-icons.tsx"

/**
 * One financial figure as a widget of its own — Revenue, Outstanding
 * receivables or Overdue receivables. The title row is the widget's header:
 * in Edit layout its handle sits before the title and its layout menu after
 * it, so the figure moves with its name and never apart from it.
 */
export const HomeMetricCard = ({
  count,
  title,
  edit,
  testId
}: {
  count: TodayCount
  /** A user's own name for the widget. */
  title?: string
  edit?: HomeCardEdit | null
  testId?: string
}) => {
  const navigate = useNavigate()
  const Glyph = count.icon
  const open = count.drillTo ? () => navigate(count.drillTo!) : undefined

  return (
    <section
      className="home-card home-metric-card"
      data-testid={testId}
      style={{ ...homeCardStyle }}
    >
      <div className="home-metric-card__head">
        {edit?.handle}
        <span
          aria-hidden
          className="home-metric-card__glyph"
          style={{ background: count.tint.bg, color: count.tint.fg }}
        >
          <Glyph width={14} height={14} />
        </span>
        <h3
          className="home-card__title"
          style={{ ...homeText.label, color: homeColor.ink, margin: 0 }}
        >
          {title ?? count.label}
        </h3>
        {count.explanation ? (
          <Tooltip title={count.explanation}>
            <button
              type="button"
              className="home-today-band__hint"
              aria-label={count.explanation}
            >
              <InfoIcon width={12} height={12} />
            </button>
          </Tooltip>
        ) : null}
        <span className="home-card__actions">{edit?.controls}</span>
      </div>
      <div
        className="home-metric-card__value"
        role={open ? "link" : undefined}
        tabIndex={open ? 0 : undefined}
        onClick={open}
        onKeyDown={
          open
            ? (event) => {
                if (event.key === "Enter") open()
              }
            : undefined
        }
        style={{
          ...homeText.metricValue,
          ...TABULAR,
          color: count.valueColor ?? homeColor.ink,
          cursor: open ? "pointer" : undefined
        }}
      >
        {count.display ?? count.value}
      </div>
    </section>
  )
}
